// services/providerService.js

import { registerProvider } from "../slices/providerRegistrySlice";

/**
 * Fetches a provider by ID:
 * - Checks the Redux store first
 * - If not present, fetches from FHIR server and registers it
 */
export const fetchProvider = async (providerId, fhirClient, getState, dispatch) => {
    if (!providerId || !fhirClient) return null;

    // Check Redux store
    const cached = getState().providers?.[providerId];
    if (cached) return cached;

    try {
        const fetched = await fhirClient.request(`Practitioner/${providerId}`);
        dispatch(registerProvider(fetched));
        return fetched;
    } catch (err) {
        if (err?.response?.status === 410) {
            console.warn(`⚠️ Practitioner/${providerId} has been deleted (410 Gone).`);
            return null;
        }

        console.error("? Failed to fetch provider:", err);
        return null;
    }
};

export const fetchProviderByReference = async (ref, fhirClient, getState, dispatch) => {
    if (!ref || !ref.startsWith("Practitioner/") || ref === "Practitioner/undefined") {
        console.warn("⚠️ Invalid provider reference:", ref);
        return null;
    }

    const providerId = ref.split("/")[1];
    if (!providerId) {
        console.warn("⚠️ Could not extract provider ID from reference:", ref);
        return null;
    }

    return fetchProvider(providerId, fhirClient, getState, dispatch);
};

export async function getCircleOfCare(patientId, fhirClient) {
    if (!patientId) {
        throw new Error("Patient ID is required to fetch circle of care.");
    }

    try {
        // Run queries in parallel
        const [
            careTeamPractitioners,
            relatedPractitioners,
            orgPractitioners,
            communicationPractitioners
        ] = await Promise.all([
            fetchCareTeamPractitioners(patientId, fhirClient),
            fetchRelatedPersonPractitioners(patientId, fhirClient),
            fetchOrganizationsPractitioners(patientId, fhirClient),
            fetchCommunicationSenders(patientId, fhirClient)
        ]);

        // Merge and deduplicate by ID
        const all = [
            ...careTeamPractitioners,
            ...relatedPractitioners,
            ...orgPractitioners,
            ...communicationPractitioners
        ];

        const deduped = Object.values(
            all.reduce((acc, p) => {
                if (p?.id) acc[p.id] = p;
                return acc;
            }, {})
        );

        console.log(careTeamPractitioners);

        return deduped;
    } catch (err) {
        console.error("❌ Failed to get circle of care:", err);
        throw err;
    }
}



// 2. Get RelatedPerson contacts → no direct Practitioner linkage but kept for symmetry
export async function fetchRelatedPersonPractitioners(patientId, client) {
    // You can optionally pull linked practitioners if your RelatedPerson has any extensions for it
    return [];
}

// 3. Get Organizations → (if they refer to Practitioners in managing orgs or contacts)
export async function fetchOrganizationsPractitioners(patientId, client) {
    console.log("Fetching organizations for patient:", patientId);
    const bundle = await client.request(`CareTeam?patient=Patient/${patientId}`);
    const teams = bundle?.entry?.map(e => e.resource) || [];

    const orgRefs = teams
        .map(t => t.managingOrganization?.reference)
        .filter(Boolean);

    // Some orgs may have contacts who are Practitioners via extensions
    const practitioners = [];
    for (const orgRef of orgRefs) {
        const org = await client.request(orgRef);
        // If org.contact[x].name → Practitioner-style info exists
        // Extend as needed depending on your FHIR structure
    }
    return practitioners;
}

// 4. Get Communication senders
export async function fetchCommunicationSenders(patientId, client, getState, dispatch) {
    console.log("Fetching communication senders for patient:", patientId);
    const bundle = await client.request(`Communication?subject=Patient/${patientId}`);
    const comms = bundle?.entry?.map((e) => e.resource) || [];

    const refs = comms
        .map((c) => c.sender?.reference)
        .filter((ref) => ref?.startsWith("Practitioner/"));

    const uniqueIds = [...new Set(refs.map((ref) => ref.split("/")[1]))];

    const practitioners = await Promise.all(
        uniqueIds.map((id) => fetchProvider(id, client, getState, dispatch))
    );

    console.log("Found practitioners from communications:", practitioners);
    return practitioners.filter(Boolean);
}


// 🔁 Utility: Deduplicated resolution of Practitioner references
export async function fetchPractitionersByRefs(refs, client) {
    const uniqueRefs = [...new Set(refs)];
    const practitioners = [];

    for (const ref of uniqueRefs) {
        try {
            const p = await client.request(ref);
            practitioners.push(p);
        } catch (err) {
            console.warn(`⚠️ Failed to fetch practitioner: ${ref}`, err);
        }
    }

    return practitioners;
}

/**
 * Search for providers (Practitioner resources) by a search term (name, family, or given).
 * Returns an array of matching Practitioner resources.
 * @param {string} searchTerm
 * @param {object} fhirClient
 * @returns {Promise<Array>}
 */
export async function searchProvidersByTerm(searchTerm, fhirClient) {
    if (!searchTerm || !fhirClient) return [];
    try {
        const bundle = await fhirClient.request(`Practitioner?name=${encodeURIComponent(searchTerm)}`);
        return bundle?.entry?.map(e => e.resource) || [];
    } catch (error) {
        console.error("Failed to search providers:", error);
        return [];
    }
}

// 1. Get CareTeam Practitioners
export async function fetchCareTeamPractitioners(patientId, client, getState, dispatch) {
    console.log("Fetching care team practitioners for patient:", patientId);
    const bundle = await client.request(`CareTeam?patient=Patient/${patientId}`);
    console.log(bundle);
    const teams = bundle?.entry?.map(e => e.resource) || [];

    const references = teams
        .flatMap(t =>
            t.participant
                ?.map(p => p.member?.reference)
                .filter(ref => ref?.startsWith("Practitioner/")) || []
        );

    console.log(references);    

    const uniqueIds = [...new Set(references.map(ref => ref.split("/")[1]))];
    console.log(uniqueIds);    
    const practitioners = await Promise.all(uniqueIds.map(id => fetchProvider(id, client, getState, dispatch)));
     console.log(practitioners);   

    return practitioners.filter(Boolean);
}


/**
 * Fetch all CarePlans for a given CareTeam.
 * @param {string} careTeamId - The ID of the CareTeam resource.
 * @param {object} client - The FHIR client instance.
 * @returns {Promise<Array>} - Array of CarePlan resources.
 */
export async function fetchCarePlans(careTeamId, client) {
    if (!careTeamId || !client) return [];
    try {
        // Search for CarePlans where careTeam reference matches the given CareTeam ID
        const bundle = await client.request(`CarePlan?care-team=CareTeam/${careTeamId}`);
        return bundle?.entry?.map(e => e.resource) || [];
    } catch (error) {
        console.error(`Failed to fetch CarePlans for CareTeam/${careTeamId}:`, error);
        return [];
    }
}

/**
 * Fetch CarePlans for a given patient that involve a specific practitioner.
 * @param {string} patientId - The Patient ID.
 * @param {string} practitionerId - The Practitioner ID.
 * @param {object} client - The FHIR client instance.
 * @returns {Promise<Array>} - Array of CarePlan resources.
 */
export async function fetchCarePlansForPractitioner(patientId, practitionerId, client) {
    // 1. Get all CareTeams for the patient
    const teamBundle = await client.request(`CareTeam?patient=Patient/${patientId}`);
    console.log(teamBundle);
    const teams = teamBundle?.entry?.map(e => e.resource) || [];
    console.log(teams);

    // 2. Filter down to only CareTeams that include this Practitioner
    const matchingTeams = teams.filter(team =>
        team.participant?.some(p => p.member?.reference === `Practitioner/${practitionerId}`)
    );

    console.log(matchingTeams);

    // 3. Fetch CarePlans that reference these CareTeams
    const carePlans = [];
    for (const team of matchingTeams) {
        const bundle = await client.request(`CarePlan?care-team=CareTeam/${team.id}`);
        const plans = bundle?.entry?.map(e => e.resource) || [];
        carePlans.push(...plans);
    }

    console.log(carePlans);

    return carePlans;
}
